import Axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Input,
  Segmented,
  Spin,
  Table,
  Tabs,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  LockOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { PageContainer, PageHeader, SectionCard } from '../../common';
import './Result360Page.css';

const formatScore = (value) =>
  Number.isFinite(value) ? value.toFixed(1) : '—';

const formatGap = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ja-JP').format(date);
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatMonth = (value) => {
  if (!/^\d{4}-\d{2}$/.test(value || '')) return '—';
  const [year, month] = value.split('-');
  return `${year}年${Number(month)}月`;
};

const gapTone = (value) => {
  if (!Number.isFinite(value) || Math.abs(value) < 0.05) return 'flat';
  return value > 0 ? 'positive' : 'negative';
};

const roundedScore = (value) =>
  Number.isFinite(value) ? Number(value.toFixed(1)) : null;

const scoreGap = (others, self) => {
  const roundedOthers = roundedScore(others);
  const roundedSelf = roundedScore(self);
  return Number.isFinite(roundedOthers) && Number.isFinite(roundedSelf)
    ? roundedScore(roundedOthers - roundedSelf)
    : null;
};

const highlightCell = (row, scoreKey) => {
  const tone = row.highlights?.[scoreKey];
  return tone
    ? { className: `fs-result__score-cell--${tone}` }
    : {};
};

const splitLabel = (label, size = 7) => {
  const value = String(label || '');
  if (value.length <= size) return [value];
  return [value.slice(0, size), value.slice(size, size * 2)];
};

const RadarChart = ({ categories, roles, benchmark }) => {
  const width = 460;
  const height = 390;
  const centerX = width / 2;
  const centerY = 185;
  const radius = 120;
  const axisCount = categories.length;
  if (!axisCount) return <Empty description="表示できるスコアがありません" />;

  const point = (axisIndex, value, extraRadius = 0) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * axisIndex) / axisCount;
    const distance = radius * (value / 5) + extraRadius;
    return [
      centerX + Math.cos(angle) * distance,
      centerY + Math.sin(angle) * distance,
    ];
  };
  const polygonPoints = (values) =>
    values
      .map((value, index) => point(index, Number.isFinite(value) ? value : 0))
      .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
      .join(' ');
  const benchmarkValues =
    benchmark && benchmark.available
      ? categories.map((category) => category.scores[benchmark.key])
      : [];
  const showBenchmark = benchmarkValues.some(Number.isFinite);

  return (
    <div className="fs-result__radar-wrap">
      <svg
        className="fs-result__radar"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="カテゴリ別スコアのレーダーチャート"
      >
        {[1, 2, 3, 4, 5].map((level) => (
          <polygon
            key={level}
            points={polygonPoints(Array(axisCount).fill(level))}
            fill={level % 2 === 0 ? 'rgba(252, 248, 238, 0.55)' : 'none'}
            stroke="var(--grey-30)"
            strokeWidth="1"
          />
        ))}
        {categories.map((category, index) => {
          const [x, y] = point(index, 5);
          const [labelX, labelY] = point(index, 5, 28);
          const anchor = labelX < centerX - 8 ? 'end' : labelX > centerX + 8 ? 'start' : 'middle';
          return (
            <g key={category.key}>
              <line
                x1={centerX}
                y1={centerY}
                x2={x}
                y2={y}
                stroke="var(--grey-30)"
                strokeWidth="1"
              />
              <text
                x={labelX}
                y={labelY - 7}
                textAnchor={anchor}
                className="fs-result__radar-label"
              >
                {splitLabel(category.name).map((line, lineIndex) => (
                  <tspan key={`${line}-${lineIndex}`} x={labelX} dy={lineIndex === 0 ? 0 : 15}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
        {showBenchmark && (
          <polygon
            points={polygonPoints(benchmarkValues)}
            fill="none"
            stroke={benchmark.color}
            strokeWidth="2"
            strokeDasharray="3 5"
            strokeLinecap="round"
          />
        )}
        {roles.map((role) => {
          const values = categories.map((category) => category.scores[role.key]);
          return (
            <g key={role.key}>
              <polygon
                points={polygonPoints(values)}
                fill={role.key === 'self' ? role.color : 'none'}
                fillOpacity={role.key === 'self' ? 0.12 : 0}
                stroke={role.color}
                strokeWidth={role.key === 'self' ? 3 : 2}
                strokeDasharray={role.key === 'sub' ? '7 5' : undefined}
              />
              {values.map((value, index) => {
                if (!Number.isFinite(value)) return null;
                const [x, y] = point(index, value);
                return (
                  <circle
                    key={`${role.key}-${categories[index].key}`}
                    cx={x}
                    cy={y}
                    r={role.key === 'self' ? 4 : 3}
                    fill={role.key === 'sub' ? 'var(--white)' : role.color}
                    stroke={role.color}
                    strokeWidth="2"
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="fs-result__legend" aria-label="チャート凡例">
        {roles.map((role) => (
          <span key={role.key}>
            <i style={{ backgroundColor: role.color }} />
            {role.label}（{role.count}名）
          </span>
        ))}
        {showBenchmark && (
          <span>
            <i className="fs-result__legend-line--benchmark" />
            {benchmark.label}（点線）
          </span>
        )}
      </div>
    </div>
  );
};

const ScoreValue = ({ value }) => (
  <span className="fs-result__score-value">{formatScore(value)}</span>
);

const BenchmarkScore = ({ value }) => (
  <span className="fs-result__benchmark-score">{formatScore(value)}</span>
);

const BenchmarkNote = ({ benchmark }) => {
  if (!benchmark || !benchmark.available) return null;
  return (
    <p className="fs-result__benchmark-note">
      全体平均は、同一実施回の対象者{benchmark.targetCount}名のうち、本人・協力者全員が回答済みの
      {benchmark.includedTargetCount}名に対する他者評価{benchmark.responseCount}件の平均です。
    </p>
  );
};

const scoreChange = (before, after) => {
  const beforeRounded = roundedScore(before);
  const afterRounded = roundedScore(after);
  return Number.isFinite(beforeRounded) && Number.isFinite(afterRounded)
    ? afterRounded - beforeRounded
    : null;
};

const formatChange = (value) => {
  if (!Number.isFinite(value)) return '—';
  const arrow = value > 0.001 ? ' ↑' : value < -0.001 ? ' ↓' : '';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}${arrow}`;
};

const ChangeValue = ({ value }) => (
  <span className={`fs-result__gap fs-result__gap--${gapTone(value)}`}>
    {formatChange(value)}
  </span>
);

const periodLabel = (period) => {
  if (!period) return '期間未設定';
  if (!period.start && !period.end) return '期間未設定';
  return `${formatDate(period.start)} 〜 ${formatDate(period.end)}`;
};

const RankingGroups = ({ groups, field, tone }) => {
  const hasItems = groups.some((group) =>
    group.sections.some((section) => section[field].length)
  );
  if (!hasItems) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="PDFの順位規則に該当する項目はありません"
      />
    );
  }

  return (
    <div className="fs-result__ranking-groups">
      {groups.map((group) => (
        <section key={group.role} className="fs-result__ranking-group">
          <h4>{group.roleLabel}の視点</h4>
          {group.sections.map((section) => {
            const questions = section[field];
            return (
              <div key={section.key} className="fs-result__ranking-section">
                {section.label && <h5>{section.label}</h5>}
                {questions.length ? (
                  <ol
                    className={`fs-result__rank-list fs-result__rank-list--${tone}`}
                  >
                    {questions.map((question) => (
                      <li key={question.no}>
                        <span>Q{question.no}</span>
                        <small
                          className="fs-result__rank-category"
                          title={question.category || '未分類'}
                        >
                          {question.category || '未分類'}
                        </small>
                        <p>{question.text}</p>
                        <b>{formatScore(question.scores[group.role])}</b>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="fs-result__ranking-empty">該当項目なし</p>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
};

const SummaryView = ({ result }) => {
  const collaboratorCount = Object.entries(result.respondentCounts)
    .filter(([key]) => key !== 'self')
    .reduce((total, [, count]) => total + count, 0);
  const activeRoles = result.roles.filter((role) => role.key !== 'self');
  const rankingGroups = Array.isArray(result.summary.rankingGroups)
    ? result.summary.rankingGroups
    : [
        {
          role: 'others',
          roleLabel: '他者総合',
          sections: [
            {
              key: 'all',
              label: null,
              strengths: result.summary.strengths || [],
              growthAreas: result.summary.growthAreas || [],
            },
          ],
        },
      ];
  const benchmark = result.benchmark;
  const hasTypeProfile = Boolean(
    result.summary.selfGrade || result.summary.othersGrade
  );
  const hasCategoryDefinitions = result.categories.some(
    (category) => category.definition
  );
  const categoryColumns = [
    {
      title: '領域',
      dataIndex: 'upperCategory',
      key: 'upperCategory',
      width: 110,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'name',
      key: 'name',
      width: 220,
    },
    ...(hasCategoryDefinitions
      ? [
          {
            title: '定義',
            dataIndex: 'definition',
            key: 'definition',
            width: 360,
            render: (definition) => (
              <span className="fs-result__category-definition">
                {definition || '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      title: '本人',
      key: 'self',
      width: 82,
      align: 'center',
      render: (_, row) => <ScoreValue value={row.scores.self} />,
    },
    ...activeRoles.map((role) => ({
      title: role.label,
      key: role.key,
      width: 82,
      align: 'center',
      render: (_, row) => <ScoreValue value={row.scores[role.key]} />,
    })),
    {
      title: '他者総合',
      key: 'others',
      width: 96,
      align: 'center',
      render: (_, row) => <ScoreValue value={row.scores.others} />,
    },
    ...(benchmark && benchmark.available
      ? [
          {
            title: '全体平均',
            key: benchmark.key,
            width: 96,
            align: 'center',
            render: (_, row) => (
              <BenchmarkScore value={row.scores[benchmark.key]} />
            ),
          },
        ]
      : []),
    {
      title: 'ギャップ',
      key: 'gap',
      width: 90,
      align: 'center',
      render: (_, row) => {
        const gap = scoreGap(row.scores.others, row.scores.self);
        return (
          <span className={`fs-result__gap fs-result__gap--${gapTone(gap)}`}>
            {formatGap(gap)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="fs-result__tab-content">
      <div className="fs-result__kpi-grid">
        <div className="fs-result__kpi">
          <span>本人 総合</span>
          <strong>{formatScore(result.summary.selfOverall)}</strong>
          <small>/ 5.0</small>
        </div>
        <div className="fs-result__kpi">
          <span>他者総合</span>
          <strong>{formatScore(result.summary.othersOverall)}</strong>
          <small>/ 5.0</small>
        </div>
        <div className="fs-result__kpi">
          <span>ギャップ（他者−本人）</span>
          <strong className={`fs-result__gap--${gapTone(result.summary.gap)}`}>
            {formatGap(result.summary.gap)}
          </strong>
        </div>
        <div className="fs-result__kpi">
          <span>協力者回答</span>
          <strong>{collaboratorCount}</strong>
          <small>名</small>
        </div>
      </div>

      <div
        className={`fs-result__summary-grid ${
          hasTypeProfile ? '' : 'fs-result__summary-grid--single'
        }`}
      >
        <SectionCard title="カテゴリ別プロフィール">
          <p className="fs-result__section-desc">
            {result.categories.length}カテゴリのスコアを役割別に比較できます。点線は同一実施回の全体平均です。
          </p>
          <RadarChart
            categories={result.categories}
            roles={result.roles}
            benchmark={benchmark}
          />
          <BenchmarkNote benchmark={benchmark} />
        </SectionCard>
        <SectionCard
          title={hasTypeProfile ? 'フォロワータイプ' : '上位領域別スコア'}
        >
          <p className="fs-result__section-desc">
            {hasTypeProfile
              ? '貢献力と提言力のバランスから現在の傾向を表示しています。'
              : '上位領域ごとに本人評価と他者総合を比較できます。'}
          </p>
          {hasTypeProfile && (
            <div className="fs-result__type-list">
              <div className="fs-result__type-card fs-result__type-card--self">
                <span>本人評価</span>
                <strong>{result.summary.selfGrade?.code || '—'}</strong>
                <b>{result.summary.selfGrade?.name || '判定なし'}</b>
              </div>
              <div className="fs-result__type-card">
                <span>他者総合</span>
                <strong>{result.summary.othersGrade?.code || '—'}</strong>
                <b>{result.summary.othersGrade?.name || '判定なし'}</b>
              </div>
            </div>
          )}
          <div className="fs-result__upper-list">
            {result.upperCategories.map((category) => (
              <div key={category.key}>
                <span>{category.name}</span>
                <b>本人 {formatScore(category.scores.self)}</b>
                <b>他者 {formatScore(category.scores.others)}</b>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="fs-result__insight-grid">
        <SectionCard title="強みとして見えている項目">
          <RankingGroups
            groups={rankingGroups}
            field="strengths"
            tone="strength"
          />
        </SectionCard>
        <SectionCard title="これから伸ばせる項目">
          <RankingGroups
            groups={rankingGroups}
            field="growthAreas"
            tone="growth"
          />
        </SectionCard>
      </div>

      <SectionCard title="カテゴリ別スコア">
        <BenchmarkNote benchmark={benchmark} />
        <Table
          rowKey="key"
          columns={categoryColumns}
          dataSource={result.categories}
          pagination={false}
          scroll={{ x: 1120 }}
          size="middle"
        />
      </SectionCard>
    </div>
  );
};

const DetailView = ({ result }) => {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const activeRoles = result.roles.filter((role) => role.key !== 'self');
  const benchmark = result.benchmark;
  const categoryNames = [
    ...new Set(result.questions.map((question) => question.category)),
  ];
  const filteredQuestions =
    categoryFilter === 'all'
      ? result.questions
      : result.questions.filter(
          (question) => question.category === categoryFilter
        );
  const columns = [
    { title: 'No.', dataIndex: 'no', key: 'no', width: 62, fixed: 'left' },
    {
      title: '領域',
      dataIndex: 'upperCategory',
      key: 'upperCategory',
      width: 100,
    },
    { title: 'カテゴリ', dataIndex: 'category', key: 'category', width: 150 },
    {
      title: '設問',
      dataIndex: 'text',
      key: 'text',
      width: 420,
      render: (text) => <span className="fs-result__question-text">{text}</span>,
    },
    {
      title: '本人',
      key: 'self',
      width: 76,
      align: 'center',
      render: (_, row) => <ScoreValue value={row.scores.self} />,
    },
    ...activeRoles.map((role) => ({
      title: role.label,
      key: role.key,
      width: 76,
      align: 'center',
      onCell: (row) => highlightCell(row, role.key),
      render: (_, row) => <ScoreValue value={row.scores[role.key]} />,
    })),
    {
      title: '他者総合',
      key: 'others',
      width: 92,
      align: 'center',
      onCell: (row) => highlightCell(row, 'others'),
      render: (_, row) => <ScoreValue value={row.scores.others} />,
    },
    ...(benchmark && benchmark.available
      ? [
          {
            title: '全体平均',
            key: benchmark.key,
            width: 92,
            align: 'center',
            render: (_, row) => (
              <BenchmarkScore value={row.scores[benchmark.key]} />
            ),
          },
        ]
      : []),
    {
      title: 'ギャップ',
      key: 'gap',
      width: 82,
      align: 'center',
      render: (_, row) => {
        const gap = scoreGap(row.scores.others, row.scores.self);
        return (
          <span
            className={`fs-result__gap fs-result__gap--${gapTone(gap)} ${
              row.prominentGap ? 'fs-result__gap--prominent' : ''
            }`}
          >
            {formatGap(gap)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="fs-result__tab-content">
      <SectionCard title="設問別スコア">
        <p className="fs-result__section-desc">
          回答値を既存PDFと同じ採点方法で集計しています。ギャップは他者総合から本人評価を引いた値です。
        </p>
        <div className="fs-result__detail-guide">
          <Tag color="green">強み</Tag>
          <span>役割別・他者総合の上位3設問</span>
          <Tag color="red">伸びしろ</Tag>
          <span>役割別・他者総合の下位3設問</span>
          <b>太字</b>
          <span>本人との絶対ギャップ上位2設問</span>
          <small>※同点の設問はすべて強調されます</small>
        </div>
        <div
          className="fs-result__filter-chips"
          role="group"
          aria-label="カテゴリで絞り込む"
        >
          <button
            type="button"
            className={categoryFilter === 'all' ? 'is-active' : ''}
            aria-pressed={categoryFilter === 'all'}
            onClick={() => setCategoryFilter('all')}
          >
            すべて <span>{result.questions.length}</span>
          </button>
          {categoryNames.map((category) => {
            const count = result.questions.filter(
              (question) => question.category === category
            ).length;
            return (
              <button
                type="button"
                key={category}
                className={categoryFilter === category ? 'is-active' : ''}
                aria-pressed={categoryFilter === category}
                onClick={() => setCategoryFilter(category)}
              >
                {category} <span>{count}</span>
              </button>
            );
          })}
        </div>
        <BenchmarkNote benchmark={benchmark} />
        <Table
          rowKey="no"
          columns={columns}
          dataSource={filteredQuestions}
          pagination={false}
          scroll={{ x: 1132 }}
          size="middle"
        />
      </SectionCard>
    </div>
  );
};

const CommentsView = ({ result }) => {
  const [questionFilter, setQuestionFilter] = useState('all');
  const [commentQuery, setCommentQuery] = useState('');
  const hasComments = result.commentGroups.some((group) => group.comments.length);
  const normalizedQuery = commentQuery.trim().toLocaleLowerCase('ja');
  const visibleCommentGroups = result.commentGroups
    .filter(
      (group) =>
        questionFilter === 'all' || String(group.no) === questionFilter
    )
    .map((group) => ({
      ...group,
      comments: normalizedQuery
        ? group.comments.filter((comment) =>
            comment.text.toLocaleLowerCase('ja').includes(normalizedQuery)
          )
        : group.comments,
    }))
    .filter((group) => group.comments.length);
  const totalCommentCount = result.commentGroups.reduce(
    (total, group) => total + group.comments.length,
    0
  );
  if (!hasComments) {
    return (
      <SectionCard>
        <Empty description="表示できるフィードバックコメントはありません" />
      </SectionCard>
    );
  }

  return (
    <div className="fs-result__tab-content fs-result__comments">
      <Alert
        type="info"
        showIcon
        icon={<LockOutlined />}
        message="コメントは匿名で表示しています"
        description="回答者の氏名・所属・メール・提出日時・回答順は表示されません。"
      />
      <SectionCard className="fs-result__comment-filter-card">
        <div
          className="fs-result__filter-chips"
          role="group"
          aria-label="自由記述設問で絞り込む"
        >
          <button
            type="button"
            className={questionFilter === 'all' ? 'is-active' : ''}
            aria-pressed={questionFilter === 'all'}
            onClick={() => setQuestionFilter('all')}
          >
            すべて <span>{totalCommentCount}</span>
          </button>
          {result.commentGroups.map((group) => (
            <button
              type="button"
              key={group.no}
              className={
                questionFilter === String(group.no) ? 'is-active' : ''
              }
              aria-pressed={questionFilter === String(group.no)}
              title={group.question}
              onClick={() => setQuestionFilter(String(group.no))}
            >
              質問{group.no} <span>{group.comments.length}</span>
            </button>
          ))}
        </div>
        <Input.Search
          allowClear
          value={commentQuery}
          aria-label="コメントを検索"
          placeholder="コメント内のキーワードを入力"
          onChange={(event) => setCommentQuery(event.target.value)}
        />
      </SectionCard>
      {visibleCommentGroups.length ? (
        visibleCommentGroups.map((group) => (
          <SectionCard
            key={group.no}
            title={`質問${group.no}`}
            extra={<Tag>{group.comments.length}件</Tag>}
          >
            <p className="fs-result__comment-question">{group.question}</p>
            <div className="fs-result__comment-list">
              {group.comments.map((comment, index) => (
                <article key={`${group.no}-${comment.role}-${index}`}>
                  <Tag color={comment.role === 'boss' ? 'default' : 'green'}>
                    {comment.roleLabel}
                  </Tag>
                  <p>{comment.text}</p>
                </article>
              ))}
            </div>
          </SectionCard>
        ))
      ) : (
        <SectionCard>
          <Empty description="該当するコメントはありません" />
        </SectionCard>
      )}
    </div>
  );
};

const RespondentsView = ({ result }) => {
  const respondentData = result.respondents || { canShowNames: false, items: [] };
  const [displayMode, setDisplayMode] = useState('anonymous');
  const named = respondentData.canShowNames && displayMode === 'named';
  const roleByKey = Object.fromEntries(
    result.roles.map((role) => [role.key, role])
  );
  const total = respondentData.items.length;

  useEffect(() => {
    if (!respondentData.canShowNames) setDisplayMode('anonymous');
  }, [respondentData.canShowNames]);

  const showIdentity = (respondent) => named || respondent.role === 'self';
  const hiddenValue = <span className="fs-result__hidden-value">非公開</span>;
  const columns = [
    {
      title: '関係',
      dataIndex: 'roleLabel',
      key: 'role',
      width: 100,
      render: (label, respondent) => (
        <Tag color={roleByKey[respondent.role]?.color}>{label}</Tag>
      ),
    },
    {
      title: '氏名',
      key: 'name',
      width: 170,
      render: (_, respondent) => (
        <strong className="fs-result__respondent-name">
          {showIdentity(respondent)
            ? respondent.name || '—'
            : respondent.anonymousLabel || '—'}
        </strong>
      ),
    },
    {
      title: '所属',
      key: 'department',
      width: 220,
      render: (_, respondent) =>
        showIdentity(respondent) ? respondent.department || '—' : hiddenValue,
    },
    {
      title: 'メール',
      key: 'email',
      width: 230,
      render: (_, respondent) =>
        showIdentity(respondent) ? respondent.email || '—' : hiddenValue,
    },
    {
      title: '回答状況',
      key: 'status',
      width: 110,
      align: 'center',
      render: () => <Tag color="success">回答完了</Tag>,
    },
    {
      title: '提出',
      key: 'submitted',
      width: 160,
      align: 'right',
      render: (_, respondent) =>
        showIdentity(respondent)
          ? formatDateTime(respondent.submittedAt)
          : formatMonth(respondent.submittedMonth),
    },
  ];

  if (!total) {
    return (
      <SectionCard>
        <Empty description="表示できる回答者情報はありません" />
      </SectionCard>
    );
  }

  return (
    <div className="fs-result__tab-content">
      <div className="fs-result__respondent-kpis">
        <div className="fs-result__kpi">
          <span>回答者数</span>
          <strong>{total}</strong>
          <small>名</small>
        </div>
        {result.roles.map((role) => (
          <div className="fs-result__kpi" key={role.key}>
            <span>
              <i
                className="fs-result__role-dot"
                style={{ backgroundColor: role.color }}
              />
              {role.label}
            </span>
            <strong>{result.respondentCounts[role.key] || 0}</strong>
            <small>名</small>
          </div>
        ))}
      </div>

      <SectionCard title="回答者一覧">
        <div className="fs-result__respondent-toolbar">
          <p className="fs-result__section-desc">
            {named
              ? '氏名表示中 — 管理者権限の範囲で氏名・所属・メール・提出日時を表示しています。'
              : '匿名表示中 — 協力者の氏名・所属・メール・提出日時（日単位）は非公開です。'}
          </p>
          {respondentData.canShowNames && (
            <div className="fs-result__name-toggle">
              <span>回答者の表示</span>
              <Segmented
                value={displayMode}
                onChange={setDisplayMode}
                options={[
                  { label: '匿名', value: 'anonymous' },
                  { label: '氏名', value: 'named' },
                ]}
              />
            </div>
          )}
        </div>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={respondentData.items}
          pagination={false}
          scroll={{ x: 990 }}
          size="middle"
        />
      </SectionCard>
    </div>
  );
};

const ComparisonView = ({ comparison }) => {
  const fallbackRole = comparison.roles.some((role) => role.key === 'others')
    ? 'others'
    : comparison.roles[0]?.key;
  const [roleKey, setRoleKey] = useState(fallbackRole);

  useEffect(() => {
    if (!comparison.roles.some((role) => role.key === roleKey)) {
      setRoleKey(fallbackRole);
    }
  }, [comparison.roles, fallbackRole, roleKey]);

  if (!comparison.available || comparison.periods.length < 2 || !roleKey) {
    return (
      <SectionCard title="時系列比較">
        <Empty description={comparison.reason || '比較できる結果がありません'} />
      </SectionCard>
    );
  }

  const [before, after] = comparison.periods;
  const role = comparison.roles.find((item) => item.key === roleKey);
  const beforeSeries = before.series[roleKey];
  const afterSeries = after.series[roleKey];
  const overallChange = scoreChange(beforeSeries.overall, afterSeries.overall);
  const categoryRows = comparison.categories.map((category, index) => ({
    ...category,
    before: beforeSeries.categoryScores[index],
    after: afterSeries.categoryScores[index],
    change: scoreChange(
      beforeSeries.categoryScores[index],
      afterSeries.categoryScores[index]
    ),
  }));
  const questionRows = comparison.questions.map((question, index) => ({
    ...question,
    before: beforeSeries.questionScores[index],
    after: afterSeries.questionScores[index],
    change: scoreChange(
      beforeSeries.questionScores[index],
      afterSeries.questionScores[index]
    ),
  }));
  const periodTitle = (point) => (
    <span className="fs-result__comparison-column-title">
      <b>{point.seasonName}</b>
      <small>{periodLabel(point.period)}</small>
    </span>
  );
  const categoryColumns = [
    {
      title: '領域',
      dataIndex: 'upperCategory',
      key: 'upperCategory',
      width: 110,
    },
    { title: 'カテゴリ', dataIndex: 'name', key: 'name', width: 160 },
    {
      title: '定義',
      dataIndex: 'definition',
      key: 'definition',
      width: 360,
      render: (definition) => (
        <span className="fs-result__comparison-definition">
          {definition || '—'}
        </span>
      ),
    },
    {
      title: periodTitle(before),
      dataIndex: 'before',
      key: 'before',
      width: 150,
      align: 'right',
      render: (value) => <ScoreValue value={value} />,
    },
    {
      title: periodTitle(after),
      dataIndex: 'after',
      key: 'after',
      width: 150,
      align: 'right',
      render: (value) => <ScoreValue value={value} />,
    },
    {
      title: '変化',
      dataIndex: 'change',
      key: 'change',
      width: 100,
      align: 'right',
      render: (value) => <ChangeValue value={value} />,
    },
  ];
  const questionColumns = [
    { title: 'No.', dataIndex: 'no', key: 'no', width: 62, fixed: 'left' },
    { title: '領域', dataIndex: 'upperCategory', key: 'upperCategory', width: 100 },
    { title: 'カテゴリ', dataIndex: 'category', key: 'category', width: 150 },
    {
      title: '設問',
      dataIndex: 'text',
      key: 'text',
      width: 430,
      render: (text) => <span className="fs-result__question-text">{text}</span>,
    },
    {
      title: periodTitle(before),
      dataIndex: 'before',
      key: 'before',
      width: 150,
      align: 'right',
      render: (value) => <ScoreValue value={value} />,
    },
    {
      title: periodTitle(after),
      dataIndex: 'after',
      key: 'after',
      width: 150,
      align: 'right',
      render: (value) => <ScoreValue value={value} />,
    },
    {
      title: '変化',
      dataIndex: 'change',
      key: 'change',
      width: 100,
      align: 'right',
      render: (value) => <ChangeValue value={value} />,
    },
  ];

  return (
    <div className="fs-result__tab-content">
      <SectionCard title="時系列比較">
        <div className="fs-result__comparison-toolbar">
          <p className="fs-result__section-desc">
            同一設問セットの完了済み結果を比較しています。役割を切り替えると、カテゴリ・設問ごとの変化を確認できます。
          </p>
          <Segmented
            value={roleKey}
            onChange={setRoleKey}
            options={comparison.roles.map((item) => ({
              label: item.label,
              value: item.key,
            }))}
          />
        </div>

        <div className="fs-result__comparison-route">
          <div>
            <i style={{ backgroundColor: '#A8A292' }} />
            <span>比較元</span>
            <strong>{before.seasonName}</strong>
            <small>{periodLabel(before.period)}</small>
          </div>
          <b>→</b>
          <div>
            <i style={{ backgroundColor: '#FF6B35' }} />
            <span>比較先</span>
            <strong>{after.seasonName}</strong>
            <small>{periodLabel(after.period)}</small>
          </div>
        </div>

        <div className="fs-result__kpi-grid fs-result__comparison-kpis">
          <div className="fs-result__kpi">
            <span>{before.seasonName}・{role.label}</span>
            <strong>{formatScore(beforeSeries.overall)}</strong>
            <small>/ 5.0</small>
          </div>
          <div className="fs-result__kpi">
            <span>{after.seasonName}・{role.label}</span>
            <strong>{formatScore(afterSeries.overall)}</strong>
            <small>/ 5.0</small>
          </div>
          <div className="fs-result__kpi">
            <span>総合スコアの変化</span>
            <strong className={`fs-result__gap--${gapTone(overallChange)}`}>
              {formatChange(overallChange)}
            </strong>
          </div>
        </div>

        {comparison.historyCount > 2 && (
          <p className="fs-result__comparison-history-note">
            完了済み結果は全{comparison.historyCount}回あります。この画面では選択中の実施回に最も近い比較可能な回を表示しています。
          </p>
        )}
      </SectionCard>

      <SectionCard title={`カテゴリ別の変化（${role.label}）`}>
        <Table
          rowKey="key"
          columns={categoryColumns}
          dataSource={categoryRows}
          pagination={false}
          scroll={{ x: 1030 }}
          size="middle"
        />
      </SectionCard>

      <SectionCard title={`設問別の変化（${role.label}）`}>
        <Table
          rowKey="no"
          columns={questionColumns}
          dataSource={questionRows}
          pagination={false}
          scroll={{ x: 1142 }}
          size="middle"
        />
      </SectionCard>
    </div>
  );
};

function Result360Page({
  surveyCode,
  fallbackTitle = '360度診断結果',
  ...props
}) {
  const tokenId = props.match.params.tokenId;
  const searchParams = new URLSearchParams(props.location.search);
  const adminViewRequested = searchParams.get('view') === 'admin';
  const openedFromAdmin360 = searchParams.get('from') === 'admin360';
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    document.body.classList.add('fs-result-page-active');
    return () => document.body.classList.remove('fs-result-page-active');
  }, []);

  useEffect(() => {
    setActiveTab('summary');
  }, [tokenId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Axios.get(`/api/results/${surveyCode}/${tokenId}`, {
      params: adminViewRequested ? { view: 'admin' } : undefined,
    })
      .then((response) => {
        if (active) setResult(response.data.result);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(
          requestError.response?.data?.error || '診断結果の読み込みに失敗しました。'
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [adminViewRequested, surveyCode, tokenId]);

  useEffect(() => {
    if (!result) return undefined;
    const previousTitle = document.title;
    document.title = [
      result.meta.title,
      result.meta.subject.name,
      result.meta.seasonName,
    ]
      .filter(Boolean)
      .join('_');
    return () => {
      document.title = previousTitle;
    };
  }, [result]);

  const returnToResults = () => {
    if (result?.meta?.viewerMode === 'admin' || adminViewRequested) {
      // 管理画面からは別タブで開く。閉じられない直接アクセス時だけ元の一覧へ遷移する。
      window.close();
      window.setTimeout(() => {
        if (!window.closed) {
          props.history.push(openedFromAdmin360 ? '/admin360' : '/status360');
        }
      }, 100);
      return;
    }
    props.history.push('/mysurvey');
  };

  const tabItems = useMemo(() => {
    if (!result) return [];
    return [
      {
        key: 'summary',
        label: 'サマリー',
        children: <SummaryView result={result} />,
      },
      {
        key: 'detail',
        label: 'スコア詳細',
        children: <DetailView result={result} />,
      },
      {
        key: 'comments',
        label: 'フィードバックコメント',
        children: <CommentsView result={result} />,
      },
      {
        key: 'respondents',
        label: '回答者一覧',
        children: <RespondentsView result={result} />,
      },
      ...(result.comparison && result.comparison.available
        ? [
            {
              key: 'compare',
              label: '時系列比較',
              children: <ComparisonView comparison={result.comparison} />,
            },
          ]
        : []),
    ];
  }, [result]);

  if (loading) {
    return (
      <PageContainer className="fs-result fs-result--loading">
        <Spin size="large" tip="診断結果を読み込んでいます..." />
      </PageContainer>
    );
  }

  if (error || !result) {
    return (
      <PageContainer className="fs-result">
        <PageHeader title={fallbackTitle} />
        <Alert
          type="error"
          showIcon
          message="結果を表示できません"
          description={error || '診断結果が見つかりません。'}
          action={
            <Button onClick={returnToResults}>
              {adminViewRequested
                ? openedFromAdmin360
                  ? 'アドミへ戻る'
                  : '回答状況へ戻る'
                : 'マイ・サーベイへ'}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const { meta } = result;
  const adminView = meta.viewerMode === 'admin';
  const period =
    meta.period.start || meta.period.end
      ? `${formatDate(meta.period.start)} 〜 ${formatDate(meta.period.end)}`
      : '';
  const subtitle = [
    meta.seasonName,
    period,
  ]
    .filter(Boolean)
    .join(' ｜ ');

  return (
    <PageContainer className="fs-result" maxWidth="1440px">
      <style media="print">
        {'@page { size: A4 landscape; margin: 10mm; }'}
      </style>
      <PageHeader
        title={meta.title}
        subtitle={subtitle}
        divider
        extra={
          <div className="fs-result__actions">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={returnToResults}
            >
              {adminView
                ? openedFromAdmin360
                  ? 'アドミへ戻る'
                  : '回答状況へ戻る'
                : '結果一覧へ戻る'}
            </Button>
            {meta.pdfAvailable && (
              <Button
                icon={<PrinterOutlined />}
                onClick={() => window.print()}
                title="ブラウザの印刷ダイアログからPDFに保存できます"
              >
                印刷・PDF出力
              </Button>
            )}
          </div>
        }
      />

      <div className="fs-result__subject-bar">
        <div>
          <span>対象者</span>
          <strong>{meta.subject.name}</strong>
        </div>
        <div>
          <span>所属</span>
          <strong>{meta.subject.department || '—'}</strong>
        </div>
        <Tag color={adminView ? 'blue' : 'orange'}>
          {adminView ? '管理者閲覧・匿名表示' : '本人向け・匿名表示'}
        </Tag>
      </div>

      <Tabs activeKey={activeTab} items={tabItems} onChange={setActiveTab} />

      <p className="fs-result__privacy-note">
        {adminView
          ? `この画面では協力者の個人情報を表示しません。元の${
              openedFromAdmin360 ? 'アドミ画面' : '回答状況画面'
            }は別タブに残っています。`
          : 'この画面では協力者の個人情報を表示しません。PDF版はマイ・サーベイの「PDFを開く」から確認できます。'}
      </p>
    </PageContainer>
  );
}

export default Result360Page;
